import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../common/Icons';
import { formatDate, formatNumber } from '../ui';
import { lotExpiryHint, lotExpiryTag, lotExpiryTone } from './lotExpiryUi';

function computeMenuStyle(triggerEl) {
  if (!triggerEl) return null;
  const rect = triggerEl.getBoundingClientRect();
  const gap = 6;
  const pad = 10;
  const minWidth = Math.max(rect.width, 300);
  const spaceBelow = window.innerHeight - rect.bottom - pad;
  const spaceAbove = rect.top - pad;
  const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
  const maxHeight = Math.min(320, openUp ? spaceAbove - gap : spaceBelow - gap);
  const height = Math.max(120, maxHeight);

  let top = openUp ? rect.top - height - gap : rect.bottom + gap;
  top = Math.max(pad, Math.min(top, window.innerHeight - height - pad));

  let left = rect.left;
  left = Math.max(pad, Math.min(left, window.innerWidth - minWidth - pad));

  return {
    top,
    left,
    width: minWidth,
    maxHeight: height,
    openUp
  };
}

export default function LotSelectField({
  lots = [],
  value = '',
  onChange,
  disabledLotIds = new Set(),
  placeholder = '— Choisir un lot —',
  copy = {},
  ariaLabel = 'Choisir un lot'
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selected = lots.find((l) => String(l.id) === String(value)) || null;

  const reposition = useCallback(() => {
    setMenuStyle(computeMenuStyle(triggerRef.current));
  }, []);

  useEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    reposition();
    const onScrollOrResize = () => reposition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, reposition, lots.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const pick = (lotId) => {
    onChange(lotId);
    setOpen(false);
  };

  const menu = open && menuStyle ? (
    <ul
      ref={menuRef}
      className={`cp-lot-select__menu cp-lot-select__menu--floating${menuStyle.openUp ? ' is-up' : ''}`}
      role="listbox"
      aria-label={ariaLabel}
      style={{
        top: `${menuStyle.top}px`,
        left: `${menuStyle.left}px`,
        width: `${menuStyle.width}px`,
        maxHeight: `${menuStyle.maxHeight}px`
      }}
    >
      {lots.length === 0 ? (
        <li className="cp-lot-select__empty">{copy.LOT_SELECT_EMPTY || 'Aucun lot disponible'}</li>
      ) : (
        lots.map((lot, index) => {
          const taken =
            disabledLotIds.has(String(lot.id)) && String(value) !== String(lot.id);
          const active = String(value) === String(lot.id);
          const isFefo = index === 0;
          return (
            <li key={lot.id} role="option" aria-selected={active}>
              <button
                type="button"
                className={`cp-lot-select__option${active ? ' is-active' : ''}${taken ? ' is-disabled' : ''}`}
                disabled={taken}
                onClick={() => pick(String(lot.id))}
              >
                <span className="cp-lot-select__option-main">
                  <span className="cp-lot-select__option-head">
                    <strong>{lot.lot_number || `#${lot.id}`}</strong>
                    {isFefo ? (
                      <span className="cp-lot-select__fefo" title={copy.LOT_FEFO_HINT}>
                        FEFO
                      </span>
                    ) : null}
                    {taken ? (
                      <span className="cp-lot-select__used">{copy.LOT_ALREADY_USED}</span>
                    ) : null}
                  </span>
                  <span className="cp-lot-select__option-meta">
                    {formatNumber(lot.quantity)} {copy.LOT_QTY_SHORT || 'dispo'}
                    {lot.expiry_date ? (
                      <>
                        {' · '}
                        {lotExpiryHint(lot.expiry_date, copy)}
                      </>
                    ) : null}
                  </span>
                </span>
                {lot.expiry_date ? (
                  <span className={`cp-expiry-chip ${lotExpiryTone(lot.expiry_date)}`}>
                    <span className="cp-expiry-chip__date">{formatDate(lot.expiry_date)}</span>
                    <span className="cp-expiry-chip__tag">{lotExpiryTag(lot.expiry_date)}</span>
                  </span>
                ) : (
                  <span className="cp-lot-select__no-exp-tag text-muted small">
                    {copy.LOT_NO_EXPIRY || 'Sans date'}
                  </span>
                )}
              </button>
            </li>
          );
        })
      )}
    </ul>
  ) : null;

  return (
    <div className={`cp-lot-select${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="cp-lot-select__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {selected ? (
          <span className="cp-lot-select__trigger-body">
            <span className="cp-lot-select__lot-num">{selected.lot_number || `#${selected.id}`}</span>
            {selected.expiry_date ? (
              <span className={`cp-expiry-chip cp-lot-select__expiry ${lotExpiryTone(selected.expiry_date)}`}>
                <span className="cp-expiry-chip__date">{formatDate(selected.expiry_date)}</span>
                <span className="cp-expiry-chip__tag">{lotExpiryTag(selected.expiry_date)}</span>
              </span>
            ) : (
              <span className="cp-lot-select__no-exp text-muted small">{copy.LOT_NO_EXPIRY || 'Sans date'}</span>
            )}
            <span className="cp-lot-select__qty-hint">
              {formatNumber(selected.quantity)} {copy.LOT_QTY_SHORT || 'dispo'}
            </span>
          </span>
        ) : (
          <span className="cp-lot-select__placeholder">{placeholder}</span>
        )}
        <Icon name="chevronDown" size={14} className="cp-lot-select__chevron" aria-hidden />
      </button>

      {menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </div>
  );
}
